/**
 * Users → Activity.
 *
 * Lives on the Users screen as a fourth tab, NOT under /admin. It answers "what
 * has each person been doing", which is a question about the people on the
 * Users screen — and it is gated by membership of the "User Activity" org
 * group rather than by an app role (see hooks/useIsInUserActivityGroup.js), so
 * it has no business behind the admin sidebar.
 *
 * Rendered inside the Users screen's own PageShell: this component is the
 * CONTENT, with no page chrome of its own.
 *
 * Backend: `GET /auth/admin/user-activity?days=&group=` (user-activity-view.md)
 * — a flat list of user×day activity rollups from the structured audit sink.
 * Read-only, admin-gated.
 *
 * What this screen is FOR (requirements, 2026-08-25):
 *   1. Cumulative activity over a trailing window AND the single-day totals
 *      inside it, broken out by loan views, document views, real data edits,
 *      loan creations, and the creation of comments, conditions and exceptions.
 *   2. Per-day activity TIMELINES per user — the sequence of what they did and
 *      when, so you can see time dispersion.
 *
 * The endpoint returns one row per user×day; each mode folds that grid along a
 * different axis (both client-side — the backend doc calls summary rows out as
 * a client-side rollup, not a new endpoint):
 *
 *   By user  — one row per PERSON, cumulative for the window; expand for the
 *              days that made it up.
 *   By day   — one row per CALENDAR DAY, everyone summed; expand for the people
 *              active in it.
 *   Timeline — one day, each person's work across the clock. This one is NOT
 *              from the rollup (which drops time-of-day): each person's day is
 *              one request to GET /auth/admin/user-activity/timeline, the
 *              complete audit record for that actor on that Eastern day across
 *              both pipelines — see useUserActivitySessions.js and
 *              USER_ACTIVITY.md §6.1.
 *
 * Layout: no `AdminCard`/`AdminGrid` — this screen IS its table, so it gets a
 * standalone block like the Correspondents list: toolbar / KPI strip / legend
 * bar / grid / count strip.
 *
 * The model, the palette + cell renderers, and the timeline live beside this
 * file (userActivityModel.js, userActivityUi.js, UserActivityTimeline.js).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { MasterDetailModule, ModuleRegistry } from "ag-grid-enterprise";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import RefreshRounded from "@mui/icons-material/Refresh";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { useSnackbar } from "notistack";

import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import SearchInput from "../../../components/inputs/SearchInput";
import { adminUserActivityApi } from "../../../api/adminPlatformApi";
import { useGroupsApi } from "../../../hooks/useGroupsHook";
import { slate, status, teal } from "../../../theme/colors";
import UserActivityTimeline from "./UserActivityTimeline";
import { useUserActivitySessions } from "./useUserActivitySessions";
import {
  OTHER_SERIES,
  SERIES,
  VIEW_SERIES,
  isSystem,
  metricsOf,
  num,
  rollupByDay,
  rollupByUser,
  summarize,
} from "./userActivityModel";
import {
  ActiveUsersCell,
  BreakdownCell,
  CountCell,
  DateTextCell,
  DayCell,
  EditsCell,
  S,
  TABLE_SX,
  UserCell,
  UserCellDense,
  fmtInt,
} from "./userActivityUi";

// Both grid modes expand a row into a nested grid — master/detail is an
// Enterprise feature the shared wrapper doesn't register (community only).
ModuleRegistry.registerModules([MasterDetailModule]);

const DAY_WINDOWS = [7, 30, 90];

/**
 * Said wherever a grid shows the rollup's day, because the grid is showing a
 * UTC bucket and a reader in New York will assume otherwise. See the "Dates &
 * times" convention in CLAUDE.md and USER_ACTIVITY.md §5.9.
 */
const DAY_BUCKET_TIP =
  "A UTC calendar day, as the rollup buckets it — in Eastern it runs from 8pm the day before. Use Timeline for real Eastern days.";
const VIEWS = { USER: "user", DAY: "day", TIMELINE: "timeline" };

// The toolbar's segmented controls. Fill and label are set TOGETHER (never a
// bare text-color override) — teal-600 under white is the passing step.
const SEG_SX = {
  "& .MuiToggleButton-root": {
    textTransform: "none",
    px: 1.5,
    py: 0.5,
    fontSize: 12.5,
    fontWeight: 600,
    color: S.muted,
    borderColor: S.border,
  },
  "& .MuiToggleButton-root.Mui-selected": {
    bgcolor: S.accent,
    color: "#fff",
    "&:hover": { bgcolor: teal[700] },
  },
};

// ── the leading columns ─────────────────────────────────────────────────────
// Fixed, NOT flexed: they are the counterpart of the label column + blank
// spacer in the expanded grid, and the two have to match for the metric columns
// to line up under their parents.
const LABEL_W = 250;
const LEAD_W = 108;

/**
 * A metric column. `flex` (not a fixed width) is what makes the set fill the
 * table; `minWidth` is the floor it can't be squeezed past, at which point the
 * grid scrolls sideways instead.
 *
 * `edge` draws the vertical rule that fences a block off from its neighbour:
 * "l" on a block's first column, "r" on its last.
 */
const metricCol = (
  headerName,
  field,
  { renderer, tip, minWidth = 100, edge, flex = 1, sortable = true } = {},
) => {
  const cls = ["num", edge === "l" && "edge-l", edge === "r" && "edge-r"]
    .filter(Boolean)
    .join(" ");
  return {
    headerName,
    field,
    flex,
    minWidth,
    sortable,
    headerClass: cls,
    cellClass: cls,
    headerTooltip: tip,
    cellRenderer: renderer || CountCell(false),
  };
};

/**
 * The metric columns, shared by both modes AND by both expanded grids, so a
 * number sits in the same place wherever you read it.
 *
 * Two fenced blocks named for what they mean — the read/write split the backend
 * doc is built around: writes are the work, reads are pulled out precisely so
 * browsing can't inflate them. Each block opens with its own Total; Breakdown
 * sits between them (it spans both), and loan reach + the grand total sit
 * outside both, so no column has to be read as "which total is this".
 */
const METRIC_COLUMNS = [
  {
    headerName: "Edits",
    groupId: "edits",
    headerTooltip:
      "Real work — everything that changed something. write_events.",
    marryChildren: true,
    children: [
      metricCol("Total", "edits", {
        renderer: EditsCell,
        minWidth: 150,
        flex: 1.3,
        edge: "l",
        tip: "write_events — every event that isn't a loan open or a file read",
      }),
      metricCol("Data", "dataEdits", {
        tip: "Loan / borrower / property / terms / document-metadata edits (conditions removed)",
      }),
      metricCol("Comments", "comments", { tip: "Comments posted" }),
      metricCol("Conditions", "conditions", {
        tip: "Condition & stip events — split out of the backend's data_updates catch-all via event_types",
      }),
      metricCol("Exceptions", "exceptions", {
        tip: "exception_updates events",
      }),
      metricCol("Creations", "creations", { edge: "r", tip: "Loans created" }),
    ],
  },
  metricCol("Breakdown", "breakdown", {
    renderer: BreakdownCell,
    minWidth: 138,
    sortable: false, // a bar has no value to sort by
    edge: "r",
    tip: "The whole event mix as one bar — hover for the counts",
  }),
  {
    headerName: "Other activity",
    groupId: "other",
    headerTooltip:
      "Real activity that is NOT editing the loan file. Counted here, never added to Edits.",
    marryChildren: true,
    children: [
      metricCol("Total", "other", {
        minWidth: 88,
        edge: "l",
        tip: "Tasks + Salesforce + automation + anything unclassified",
      }),
      metricCol("Tasks", "tasks", { tip: "task_updates — task-list work" }),
      metricCol("Salesforce", "salesforce", {
        minWidth: 106,
        tip: "Chatter posts + SF field history — work done in Salesforce, not the LOS",
      }),
      metricCol("Automation", "automation", {
        minWidth: 110,
        edge: "r",
        tip: "Agent / AI / Ocrolus / box_sync writes",
      }),
    ],
  },
  {
    headerName: "Reads",
    groupId: "views",
    headerTooltip:
      "Opening loans and files. Kept out of Edits on purpose — this is the split that stops browsing reading as productivity.",
    marryChildren: true,
    children: [
      metricCol("Total", "views", {
        minWidth: 88,
        edge: "l",
        tip: "view_events — loan opens + document views + downloads",
      }),
      metricCol("Total Loan Views", "loanViews", { minWidth: 136 }),
      metricCol("Doc views", "docViews"),
      metricCol("Downloads", "docDownloads", {
        minWidth: 108,
        edge: "r",
        tip: "A COPY left the system. File-count, not click-count — one zip download emits one event PER FILE.",
      }),
    ],
  },
  metricCol("Loans", "loans", {
    minWidth: 88,
    tip: "Distinct loans touched (a union, not a sum of the rows below)",
  }),
  metricCol("All events", "total", {
    minWidth: 104,
    tip: "total_events — edits + views",
  }),
];

/** One KPI in the strip above the table. */
const Stat = ({ value, label, sub, accent, muted, last }) => (
  <Box
    sx={{
      px: 2.25,
      py: 1.75,
      flex: 1,
      minWidth: 150,
      borderRight: last ? "none" : `1px solid ${S.border}`,
    }}
  >
    <Txt
      sx={{
        fontSize: 20,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        // eslint-disable-next-line no-nested-ternary
        color: accent ? S.accent : muted ? S.muted : slate[900],
      }}
    >
      {value}
    </Txt>
    <Txt
      sx={{
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: ".05em",
        textTransform: "uppercase",
        color: S.label,
        mt: 0.25,
      }}
    >
      {label}
    </Txt>
    <Txt sx={{ display: "block", fontSize: 11.5, color: S.faint }}>{sub}</Txt>
  </Box>
);

export default function UserActivityView() {
  const { enqueueSnackbar } = useSnackbar();
  const { listGroups } = useGroupsApi();
  // The Timeline's per-person days — see useUserActivitySessions.js. Held here
  // so the cache survives switching modes and days; `refresh` clears it.
  const sessions = useUserActivitySessions();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [days, setDays] = useState(7);
  const [group, setGroup] = useState("");
  const [groups, setGroups] = useState([]);
  const [view, setView] = useState(VIEWS.USER);
  const [search, setSearch] = useState("");
  const [includeSystem, setIncludeSystem] = useState(true);
  const [timelineDay, setTimelineDay] = useState("");
  const [timelineOpen, setTimelineOpen] = useState(() => new Set());

  // Every fetch gets a ticket; only the newest one may write. Switching the
  // group (or window) twice in quick succession fires two requests, and
  // without this the slower, older one could land last and show the wrong
  // group's rows under the new group's label.
  const ticket = useRef(0);
  const load = useCallback(() => {
    const mine = ++ticket.current;
    setLoading(true);
    adminUserActivityApi
      .list({ days, group: group || undefined })
      .then((rows) => {
        if (mine === ticket.current) setData(rows);
      })
      .catch((err) => {
        if (mine !== ticket.current) return;
        setData(null);
        enqueueSnackbar(
          err?.response?.data?.detail || "Couldn't load user activity",
          { variant: "error" },
        );
      })
      .finally(() => {
        if (mine === ticket.current) setLoading(false);
      });
  }, [days, group, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  // The group picker. Any org-group name is a valid `group` value, so the list
  // comes straight from /groups — silently absent if the call isn't permitted,
  // since the picker is a convenience and "All groups" is the real default.
  useEffect(() => {
    let alive = true;
    listGroups()
      .then((rows) => {
        if (alive) setGroups(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [listGroups]);

  const refresh = () => {
    setSpinning(true);
    window.setTimeout(() => setSpinning(false), 600);
    // The timeline's cached days go too, and anything still in flight is
    // disowned — a stale response must never land on a refreshed day.
    sessions.clear();
    load();
  };

  // The served rows, each flattened to the shared metric vocabulary.
  const dayRows = useMemo(() => {
    const raw = Array.isArray(data) ? data : data?.rows || data?.entries || [];
    return raw.map((r) => ({ ...r, ...metricsOf(r) }));
  }, [data]);

  /**
   * Search and the automation switch filter the SOURCE rows, not the rendered
   * grid. AG Grid's quick filter only sees top-level rows, which in By-day mode
   * are calendar days with no person on them — and filtering the source is the
   * more useful semantic anyway: searching a name re-totals every day, and
   * every KPI, around just that person.
   */
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return dayRows.filter((r) => {
      if (!includeSystem && isSystem(r)) return false;
      if (!q) return true;
      return `${r.userName || ""} ${r.user || ""}`.toLowerCase().includes(q);
    });
  }, [dayRows, search, includeSystem]);

  const stats = useMemo(() => summarize(filteredRows), [filteredRows]);

  /**
   * `unclassified` is the backend's canary — every audit topic is mapped
   * explicitly and an unknown one lands here instead of silently inflating the
   * edits number. Non-zero means a new topic shipped without a mapping, so the
   * screen says so rather than absorbing it.
   */
  const unclassified = useMemo(
    () => filteredRows.reduce((n, r) => n + num(r.unclassified), 0),
    [filteredRows],
  );

  // `maxEdits` scales the mini-bar in the Edits column, against the busiest
  // PERSON — automation would otherwise flatten every human bar to a pixel.
  const withScale = (list) => {
    const max = Math.max(
      0,
      ...list.filter((r) => !isSystem(r)).map((r) => num(r.edits)),
    );
    return list.map((r) => ({ ...r, maxEdits: max }));
  };

  const userRows = useMemo(
    () => withScale(rollupByUser(filteredRows)),
    [filteredRows],
  );
  const dayGroupRows = useMemo(
    () => withScale(rollupByDay(filteredRows)),
    [filteredRows],
  );

  const byUser = view === VIEWS.USER;
  const isTimeline = view === VIEWS.TIMELINE;
  const rows = byUser ? userRows : dayGroupRows;

  const columnDefs = useMemo(() => {
    // The expander lives on the leading column: a person opens into their days,
    // a day opens into its people.
    const lead = byUser
      ? {
          headerName: "User",
          colId: "user",
          valueGetter: (p) => p.data?.userName || p.data?.user || "—",
          cellRendererParams: { innerRenderer: UserCell },
        }
      : {
          headerName: "Date",
          colId: "day",
          headerTooltip: DAY_BUCKET_TIP,
          valueGetter: (p) => p.data?.date || "",
          cellRendererParams: { innerRenderer: DayCell },
        };

    return [
      {
        ...lead,
        width: LABEL_W,
        pinned: "left", // the who/when stays put while the metrics scroll
        cellRenderer: "agGroupCellRenderer",
      },
      byUser
        ? {
            headerName: "Active days",
            field: "activeDays",
            width: LEAD_W,
            headerClass: "num",
            cellClass: "num",
            headerTooltip: "Days in this window with at least one event",
            cellRenderer: CountCell(false),
          }
        : {
            headerName: "Active users",
            field: "activeUsers",
            width: LEAD_W,
            headerClass: "num",
            cellClass: "num",
            headerTooltip: "People active that day (automation counted beside)",
            cellRenderer: ActiveUsersCell,
          },
      ...METRIC_COLUMNS,
    ];
  }, [byUser]);

  /**
   * The expanded grid. Same metric columns, so every figure sits directly under
   * its parent — the label column takes the pinned column's width and a blank
   * spacer takes the lead column's, which is what makes them line up.
   */
  const detailCellRendererParams = useMemo(
    () => ({
      detailGridOptions: {
        domLayout: "autoHeight",
        headerHeight: 28,
        rowHeight: byUser ? 36 : 44,
        suppressCellFocus: true,
        defaultColDef: {
          sortable: true,
          resizable: false,
          suppressHeaderMenuButton: true,
          suppressMovable: true,
        },
        getRowStyle: (p) =>
          isSystem(p.data) ? { background: S.sysRow } : undefined,
        columnDefs: [
          byUser
            ? {
                headerName: "Day",
                field: "date",
                width: LABEL_W,
                headerTooltip: DAY_BUCKET_TIP,
                cellStyle: { paddingLeft: "34px" },
                cellRenderer: DateTextCell,
              }
            : {
                headerName: "User",
                colId: "user",
                width: LABEL_W,
                cellStyle: { paddingLeft: "34px" },
                valueGetter: (p) => p.data?.userName || p.data?.user || "—",
                cellRenderer: UserCellDense,
              },
          { colId: "spacer", width: LEAD_W, headerName: "", sortable: false },
          ...METRIC_COLUMNS,
        ],
      },
      getDetailRowData: (p) =>
        p.successCallback(byUser ? p.data.days || [] : p.data.entries || []),
    }),
    [byUser],
  );

  const dayCount = new Set(filteredRows.map((r) => r.date)).size;
  // eslint-disable-next-line no-nested-ternary
  const footer = isTimeline
    ? `Timeline · ${dayCount} ${dayCount === 1 ? "day" : "days"} in window · pick a day, then a person for their session sequence`
    : byUser
      ? `${fmtInt(userRows.length)} ${userRows.length === 1 ? "user" : "users"} · cumulative over the last ${days} days · expand a row for its days`
      : `${fmtInt(dayGroupRows.length)} ${dayGroupRows.length === 1 ? "day" : "days"} · expand a day to see everyone's activity within it`;

  return (
    // `mt: 2` matches the other Users tabs — this is one more view under the
    // same tab bar, not a page.
    <Box sx={{ mt: 2 }}>
      {/* The table's own block. It carries its own toolbar, KPI strip and count
          strip — the Users screen supplies only the page title and tab bar
          above it. */}
      <Box
        sx={{
          bgcolor: S.surface,
          border: `1px solid ${S.border}`,
          borderRadius: "12px",
          boxShadow:
            "0 1px 2px rgba(16,32,48,.05), 0 2px 8px rgba(16,32,48,.04)",
          overflow: "hidden",
        }}
      >
        {/* ── toolbar ── */}
        <Flx
          ac
          g={1.25}
          wrap
          sx={{
            px: 2.25,
            py: 1.5,
            borderBottom: `1px solid ${S.border}`,
            position: "relative",
          }}
        >
          {/* The fetch takes about a second (longer with a group filter), and
              the rows it replaces stay on screen meanwhile — so say something
              is happening: a bar on the toolbar's bottom edge, sitting on top
              of the border so nothing shifts, and the body dimmed and inert
              below it (the grid's own overlay only covers the grid, not the KPI
              strip or the Timeline). */}
          {loading ? (
            <LinearProgress
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -1,
                height: 2,
                zIndex: 1,
              }}
            />
          ) : null}
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search user or email…"
            size="small"
            sx={{ width: 240 }}
          />
          {/* Server-side: the endpoint restricts rows to the group's members. */}
          <TextField
            select
            size="small"
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            // `displayEmpty` is what makes the "" option render its label rather
            // than an empty box — without it MUI treats "no value" as nothing to
            // draw, and the control's default state looks broken.
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 170, "& .MuiSelect-select": { fontSize: 13 } }}
          >
            <MenuItem value="" sx={{ fontSize: 13 }}>
              All groups
            </MenuItem>
            {groups.map((g) => (
              <MenuItem
                key={g.name || g._id}
                value={g.name}
                sx={{ fontSize: 13 }}
              >
                {g.name}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flex: 1 }} />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
            sx={SEG_SX}
          >
            <ToggleButton value={VIEWS.USER}>By user</ToggleButton>
            <ToggleButton value={VIEWS.DAY}>By day</ToggleButton>
            <ToggleButton value={VIEWS.TIMELINE}>Timeline</ToggleButton>
          </ToggleButtonGroup>
          {/* The window is server-side — changing it re-calls the endpoint. */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(_, v) => v && setDays(v)}
            sx={SEG_SX}
          >
            {DAY_WINDOWS.map((d) => (
              <ToggleButton key={d} value={d}>
                {d}d
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={refresh}
                disabled={loading}
                sx={{
                  border: `1px solid ${S.border}`,
                  borderRadius: "8px",
                  color: S.muted,
                  "&:hover": { color: S.accent, borderColor: S.accent },
                  "& svg": spinning
                    ? { animation: "uaSpin .6s linear" }
                    : undefined,
                  "@keyframes uaSpin": { to: { transform: "rotate(360deg)" } },
                }}
              >
                <RefreshRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Flx>

        <Box
          sx={{
            opacity: loading ? 0.45 : 1,
            pointerEvents: loading ? "none" : "auto",
            transition: "opacity .15s",
          }}
        >
          {/* ── KPI strip: the window at a glance, people and automation apart ── */}
          <Flx wrap sx={{ borderBottom: `1px solid ${S.border}` }}>
            <Stat
              value={fmtInt(stats.activeUsers)}
              label="Active users"
              sub={`last ${days} days`}
            />
            <Stat
              value={fmtInt(stats.edits)}
              label="Edits"
              sub="by people"
              accent
            />
            <Stat
              value={fmtInt(stats.views)}
              label="Reads"
              sub="loan opens + doc views + downloads"
            />
            <Stat
              value={fmtInt(stats.loanTouches)}
              label="Loan touches"
              sub="user × day, distinct loans"
            />
            <Stat
              value={fmtInt(stats.systemEvents)}
              label="System events"
              sub="automation"
              muted
              last
            />
          </Flx>

          {/* ── legend + the automation switch ── */}
          <Flx
            ac
            g={2.25}
            wrap
            sx={{ px: 2.25, py: 1.25, borderBottom: `1px solid ${S.border}` }}
          >
            <Flx ac g={1.75} wrap>
              {[...SERIES, ...OTHER_SERIES, ...VIEW_SERIES].map((s) => (
                <Flx key={s.key} ac g={0.75}>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "2.5px",
                      bgcolor: s.color,
                      flexShrink: 0,
                    }}
                  />
                  <Txt sx={{ fontSize: 11.5, color: S.muted }}>{s.label}</Txt>
                </Flx>
              ))}
              <Txt sx={{ fontSize: 11.5, color: S.faint }}>
                · only the first five count as edits
              </Txt>
            </Flx>

            <Box sx={{ flex: 1 }} />

            {unclassified > 0 ? (
              <Tooltip title="An audit topic has no category mapping on the backend, so its events can't be attributed. Counted under Other activity — tell the backend team.">
                <Flx
                  ac
                  g={0.5}
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: "6px",
                    bgcolor: status.warning.bg,
                    color: status.warning.fg,
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: "help",
                  }}
                >
                  <WarningAmberRounded sx={{ fontSize: 14 }} />
                  {fmtInt(unclassified)} unclassified
                </Flx>
              </Tooltip>
            ) : null}

            <Flx
              ac
              g={0.5}
              component="label"
              sx={{ cursor: "pointer", userSelect: "none" }}
            >
              <Switch
                size="small"
                checked={includeSystem}
                onChange={(e) => setIncludeSystem(e.target.checked)}
              />
              <Txt sx={{ fontSize: 12.5, color: S.muted }}>
                Include automation
              </Txt>
            </Flx>
          </Flx>

          {/* ── the view ── */}
          {isTimeline ? (
            <UserActivityTimeline
              rows={filteredRows}
              day={timelineDay}
              onDayChange={(d) => {
                setTimelineDay(d);
                setTimelineOpen(new Set());
              }}
              expanded={timelineOpen}
              sessions={sessions}
              onToggleUser={(u) =>
                setTimelineOpen((prev) => {
                  const next = new Set(prev);
                  if (next.has(u)) next.delete(u);
                  else next.add(u);
                  return next;
                })
              }
            />
          ) : (
            /* No height cap: the grid grows to its rows and the PAGE scrolls, so
             the table is never a scroller inside a scroller. Affordable because
             both modes group — By user is one row per person, By day one per
             calendar day — so the top level is tens of rows, not the raw
             hundreds of user×day entries. The minHeight only reserves room for
             the empty overlay, which an auto-height grid gives no space of its
             own. */
            <Box sx={{ minHeight: rows.length ? 0 : 180 }}>
              <AgGridTableWrapper noBorder sx={TABLE_SX}>
                <AgGridReact
                  domLayout="autoHeight"
                  rowData={rows}
                  columnDefs={columnDefs}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                    suppressHeaderMenuButton: true,
                    suppressHeaderFilterButton: true,
                    suppressMovable: true,
                  }}
                  getRowId={(p) => (byUser ? p.data.user : p.data.date)}
                  loading={loading}
                  rowHeight={46}
                  headerHeight={34}
                  groupHeaderHeight={26}
                  masterDetail
                  detailCellRendererParams={detailCellRendererParams}
                  detailRowAutoHeight
                  // Automation reads as automation: same grid, its own faint band.
                  getRowStyle={(p) =>
                    byUser && isSystem(p.data)
                      ? { background: S.sysRow }
                      : undefined
                  }
                  overlayNoRowsTemplate={`<span style="font-size:13px;color:${S.label}">No activity recorded for this window.</span>`}
                />
              </AgGridTableWrapper>
            </Box>
          )}
        </Box>

        <Flx
          ac
          g={0.75}
          sx={{ px: 2.25, py: 1.25, borderTop: `1px solid ${S.border}` }}
        >
          <Txt sx={{ fontSize: 12, color: S.label }}>{footer}</Txt>
        </Flx>
      </Box>
    </Box>
  );
}
